import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { Token, TokenCandle, TradeOrder } from "~/types";

import {
    AreaChart,
    Area,
    Tooltip,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from "recharts";
import { isEmpty, isNil } from "lodash-es";
import { ORDER_DIRECTION } from "../constants";
import { formatPrice } from "~/utils/number-format";
import { hapticFeedback } from "@telegram-apps/sdk-react";

type MarkPointData = {
    type: number;
    ts: number;
};

const isNotNil = (value: MarkPointData | null): value is MarkPointData => {
    return value != null;
};

const CustomTooltip = ({ active, payload }: any) => {
    useEffect(() => {
        if (active) {
            hapticFeedback.impactOccurred("light");
        }
    }, [active, payload?.[0]?.payload?.ts]);

    if (active && payload && payload.length) {
        const p = payload[0].payload;
        return (
            <div className="bg-[rgb(0,0,0)] p-1 text-direction2 rounded text-white">
                <div>{dayjs(p.ts * 1000).format("MM-DD HH:mm")} (UTC)</div>
                <div>{formatPrice(p.close)}</div>
            </div>
        );
    }

    return <></>;
};

const KLineLineChart: React.FC<{
    tokenInfo: Token;
    initialData?: TokenCandle[];
    orderList: TradeOrder[];
}> = ({ tokenInfo = {} as Token, initialData = [], orderList = [] }) => {
    const change = tokenInfo?.token_market?.change;
    const candlesData = initialData ?? [];
    const data = candlesData.map((item) => {
        return {
            open: item.open_usd,
            close: item.close_usd,
            ts: item.ts,
        };
    });

    const markPoints = useMemo(() => {
        if (!isEmpty(candlesData)) {
            const startTime = candlesData[0]?.ts;
            const endTime = candlesData[candlesData.length - 1]?.ts;

            if (!isNil(startTime) && !isNil(endTime)) {
                return (orderList ?? [])
                    .filter((item) => {
                        return (
                            startTime <= Number(item.create_time) &&
                            Number(item.create_time) <= endTime
                        );
                    })
                    .map((item) => {
                        const createTime = +item.create_time;
                        const p = candlesData.find(
                            (item: any, index: number) => {
                                const nextTimestamp =
                                    candlesData[index + 1]?.ts;
                                if (isNil(nextTimestamp)) return null;
                                return (
                                    createTime >= item.ts &&
                                    createTime < nextTimestamp
                                );
                            }
                        );

                        if (isNil(p)) return null;

                        return {
                            type: item.direction,
                            ts: p.ts,
                        } as MarkPointData;
                    })
                    .filter(isNotNil);
            }
        }

        return [];
    }, [candlesData, orderList]);

    const CustomDot = (props: any) => {
        const { cx, cy, payload } = props;
        const timestamp: number = payload.ts;

        const markPoint = markPoints.find((item) => item.ts === timestamp);

        if (markPoint) {
            if (markPoint.type === ORDER_DIRECTION.buy) {
                return (
                    <svg
                        x={cx - 7}
                        y={cy - 7}
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <rect
                            x="0.5"
                            y="0.5"
                            width="13"
                            height="13"
                            rx="2.5"
                            fill="#00DD69"
                        />
                        <rect
                            x="0.5"
                            y="0.5"
                            width="13"
                            height="13"
                            rx="2.5"
                            stroke="white"
                        />
                        <path
                            d="M4.70654 10.8755V9.80127H7.01074C7.52279 9.80127 7.9113 9.69743 8.17627 9.48975C8.44124 9.27848 8.57373 8.97591 8.57373 8.58203V8.57129C8.57373 8.3099 8.51286 8.08968 8.39111 7.91064C8.26937 7.73161 8.08675 7.59733 7.84326 7.50781C7.60335 7.41829 7.30615 7.37354 6.95166 7.37354H4.70654V6.39062H6.73682C7.22738 6.39062 7.59977 6.29753 7.854 6.11133C8.11182 5.92513 8.24072 5.65658 8.24072 5.30566V5.29492C8.24072 4.95117 8.12435 4.68262 7.8916 4.48926C7.65885 4.2959 7.33301 4.19922 6.91406 4.19922H4.70654V3.125H7.24707C7.74121 3.125 8.16374 3.20378 8.51465 3.36133C8.86556 3.5153 9.1359 3.7373 9.32568 4.02734C9.51546 4.3138 9.61035 4.65576 9.61035 5.05322V5.06396C9.61035 5.3361 9.54769 5.59033 9.42236 5.82666C9.30062 6.06299 9.1359 6.25993 8.92822 6.41748C8.72412 6.57503 8.49137 6.67529 8.22998 6.71826V6.81494C8.57731 6.84717 8.88167 6.94743 9.14307 7.11572C9.40804 7.28044 9.61393 7.49707 9.76074 7.76562C9.91113 8.03418 9.98633 8.33496 9.98633 8.66797V8.67871C9.98633 9.12988 9.87891 9.52018 9.66406 9.84961C9.4528 10.179 9.14844 10.4333 8.75098 10.6123C8.35352 10.7878 7.87907 10.8755 7.32764 10.8755H4.70654ZM4.01367 10.8755V3.125H5.39941V10.8755H4.01367Z"
                            fill="white"
                        />
                    </svg>
                );
            }

            return (
                <svg
                    x={cx - 7}
                    y={cy - 7}
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect
                        x="0.5"
                        y="0.5"
                        width="13"
                        height="13"
                        rx="2.5"
                        fill="#FE3B30"
                    />
                    <rect
                        x="0.5"
                        y="0.5"
                        width="13"
                        height="13"
                        rx="2.5"
                        stroke="white"
                    />
                    <path
                        d="M7.01025 11.0688C6.40869 11.0688 5.8859 10.9757 5.44189 10.7896C5.00146 10.6034 4.65413 10.3438 4.3999 10.0107C4.14567 9.67773 4.00244 9.28923 3.97021 8.84521L3.96484 8.77002H5.30762L5.31299 8.82373C5.33447 9.04215 5.42041 9.23193 5.5708 9.39307C5.72477 9.5542 5.92887 9.68131 6.18311 9.77441C6.43734 9.86393 6.72738 9.90869 7.05322 9.90869C7.36475 9.90869 7.64046 9.86035 7.88037 9.76367C8.12028 9.66699 8.30827 9.53451 8.44434 9.36621C8.5804 9.19434 8.64844 8.9974 8.64844 8.77539V8.77002C8.64844 8.49072 8.53923 8.26335 8.3208 8.08789C8.10238 7.90885 7.74251 7.76562 7.24121 7.6582L6.40869 7.48633C5.61377 7.31803 5.03548 7.04948 4.67383 6.68066C4.31576 6.30827 4.13672 5.83382 4.13672 5.25732V5.25195C4.13672 4.79004 4.25846 4.38542 4.50195 4.03809C4.74902 3.69076 5.0874 3.42041 5.51709 3.22705C5.95036 3.03011 6.44629 2.93164 7.00488 2.93164C7.5778 2.93164 8.07373 3.02832 8.49268 3.22168C8.91162 3.41146 9.24105 3.67106 9.48096 4.00049C9.72087 4.32992 9.85514 4.70231 9.88379 5.11768L9.88916 5.1875H8.56787L8.55713 5.12305C8.5249 4.91895 8.44076 4.7417 8.30469 4.59131C8.1722 4.43734 7.99495 4.31559 7.77295 4.22607C7.55094 4.13656 7.29134 4.0918 6.99414 4.0918C6.71126 4.0918 6.45882 4.13656 6.23682 4.22607C6.01481 4.31201 5.83936 4.43376 5.71045 4.59131C5.58512 4.74886 5.52246 4.93864 5.52246 5.16064V5.16602C5.52246 5.43815 5.62809 5.66374 5.83936 5.84277C6.0542 6.02181 6.40332 6.16146 6.88672 6.26172L7.71924 6.43896C8.26351 6.55355 8.70573 6.70573 9.0459 6.89551C9.38607 7.08529 9.63493 7.32161 9.79248 7.60449C9.95361 7.88379 10.0342 8.22038 10.0342 8.61426V8.61963C10.0342 9.12093 9.91064 9.5542 9.66357 9.91943C9.42008 10.2847 9.07096 10.5675 8.61621 10.7681C8.16504 10.9686 7.62972 11.0688 7.01025 11.0688Z"
                        fill="white"
                    />
                </svg>
            );
        } else {
            return <></>;
        }
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart
                data={data}
                margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
            >
                <defs>
                    <linearGradient id="colorUp" x1="0" y1="0" x2="0" y2="1">
                        <stop
                            offset="5%"
                            stopColor="#00dd69"
                            stopOpacity={0.2}
                        />
                        <stop
                            offset="95%"
                            stopColor="#00dd69"
                            stopOpacity={0}
                        />
                    </linearGradient>
                    <linearGradient id="colorEq" x1="0" y1="0" x2="0" y2="1">
                        <stop
                            offset="5%"
                            stopColor="#707579"
                            stopOpacity={0.2}
                        />
                        <stop
                            offset="95%"
                            stopColor="#707579"
                            stopOpacity={0}
                        />
                    </linearGradient>
                    <linearGradient id="colorDown" x1="0" y1="0" x2="0" y2="1">
                        <stop
                            offset="5%"
                            stopColor="#fe3b30"
                            stopOpacity={0.2}
                        />
                        <stop
                            offset="95%"
                            stopColor="#fe3b30"
                            stopOpacity={0}
                        />
                    </linearGradient>
                </defs>
                <XAxis dataKey="ts" hide={true} />
                <YAxis hide={true} />
                <Tooltip
                    cursor={{
                        stroke:
                            change > 0
                                ? "#00DD69"
                                : change < 0
                                  ? "#FE3B30"
                                  : "#707579",
                        strokeWidth: 1,
                        strokeDasharray: 3,
                    }}
                    content={<CustomTooltip />}
                />
                <Area
                    type="monotone"
                    dataKey="close"
                    stroke={
                        change > 0
                            ? "#00DD69"
                            : change < 0
                              ? "#FE3B30"
                              : "#707579"
                    }
                    strokeWidth={2}
                    fillOpacity={1}
                    fill={
                        change > 0
                            ? "url(#colorUp)"
                            : change < 0
                              ? "url(#colorDown)"
                              : "url(#colorEq)"
                    }
                    isAnimationActive={false}
                    dot={<CustomDot />}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default KLineLineChart;
